import { Extension } from '@tiptap/core'
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion'
import type { EmailVariable } from '@/views/email/constants/variables'

export interface ConsentVariableSuggestionState {
  items: EmailVariable[]
  rect: DOMRect | null
  command: (variable: EmailVariable) => void
}

export interface ConsentVariableSuggestionHandlers {
  isOpen: () => boolean
  onStart: (state: ConsentVariableSuggestionState) => void
  onUpdate: (state: ConsentVariableSuggestionState) => void
  onExit: () => void
  onArrowDown: () => boolean
  onArrowUp: () => boolean
  onEnter: () => boolean
}

function filterVariables(variables: EmailVariable[], query: string): EmailVariable[] {
  const q = query.toLowerCase()
  return variables.filter((v) => v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q))
}

export function createConsentVariableSuggestion(
  getVariables: () => EmailVariable[],
  handlers: ConsentVariableSuggestionHandlers
): Extension {
  return Extension.create({
    name: 'consentVariableSuggestion',

    addProseMirrorPlugins() {
      const options: Omit<SuggestionOptions<EmailVariable, EmailVariable>, 'editor'> = {
        char: '@',
        allowSpaces: false,
        allowedPrefixes: null,
        dismissOnOutsideClick: false,
        items: ({ query }) => filterVariables(getVariables(), query),
        command: ({ editor, range, props }) => {
          editor.chain().focus().deleteRange(range).insertConsentVariable(props.key).run()
        },
        render: () => ({
          onStart: (props) =>
            handlers.onStart({
              items: props.items,
              rect: props.clientRect?.() ?? null,
              command: props.command
            }),
          onUpdate: (props) =>
            handlers.onUpdate({
              items: props.items,
              rect: props.clientRect?.() ?? null,
              command: props.command
            }),
          onKeyDown: (props) => {
            if (!handlers.isOpen()) return false
            switch (props.event.key) {
              case 'ArrowDown':
                return handlers.onArrowDown()
              case 'ArrowUp':
                return handlers.onArrowUp()
              case 'Enter':
              case 'Tab':
                return handlers.onEnter()
              case 'Escape':
                handlers.onExit()
                return true
              default:
                return false
            }
          },
          onExit: () => handlers.onExit()
        })
      }

      return [Suggestion({ editor: this.editor, ...options })]
    }
  })
}
